import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { scorecardKeys } from '../Scorecard/queryKeys';
import { CreateInvestmentInput, DeleteInvestmentInput, UpdateInvestmentInput } from '../generated/graphql';
import {
  clientCloseInvestment,
  CloseInvestmentInput,
  clientCreateInvestment,
  clientDeleteInvestment,
  clientUpdateInvestment,
  useGraphqlClient,
} from '../graphql';
import { investmentKeys } from './queryKeys';
import { closedAccountsQueryKey, ttDetailsKeys } from '../TtDetails';

export const useCreateInvestment = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();
  const [householdID, setHouseholdID] = useState<string>();

  const createInvestment = useMutation({
    retry: 3,
    mutationKey: ['useCreateInvestment'],
    mutationFn: (input: CreateInvestmentInput) => {
      setHouseholdID(input.householdID);
      return clientCreateInvestment(gqlClient, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(investmentKeys.investments(householdID));
      queryClient.invalidateQueries(investmentKeys.investmentTypes(householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(householdID));
    },
  });

  return { createInvestment };
};

export const useUpdateInvestment = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();
  const [householdID, setHouseholdID] = useState<string>();

  const updateInvestment = useMutation({
    retry: 3,
    mutationKey: ['useUpdateInvestment'],
    mutationFn: (input: UpdateInvestmentInput) => {
      setHouseholdID(input.householdID);
      return clientUpdateInvestment(gqlClient, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(investmentKeys.investments(householdID));
      queryClient.invalidateQueries(investmentKeys.investmentTypes(householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(householdID));
    },
  });

  return { updateInvestment };
};

export const useDeleteInvestment = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();
  const [householdID, setHouseholdID] = useState<string>();

  const deleteInvestment = useMutation({
    retry: 3,
    mutationKey: ['useDeleteInvestment'],
    mutationFn: (input: DeleteInvestmentInput) => {
      setHouseholdID(input.householdID);
      return clientDeleteInvestment(gqlClient, input);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries(investmentKeys.investments(householdID));
      queryClient.invalidateQueries(investmentKeys.investmentTypes(householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(householdID));
      await queryClient.invalidateQueries(ttDetailsKeys.base);
    },
  });

  return { deleteInvestment };
};

export const useCloseInvestment = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();

  const closeInvestment = useMutation({
    mutationFn: (input: CloseInvestmentInput) => clientCloseInvestment(gqlClient, input),
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries(investmentKeys.investments(variables.householdID));
      queryClient.invalidateQueries(investmentKeys.investmentTypes(variables.householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(variables.householdID));
      queryClient.invalidateQueries(closedAccountsQueryKey(variables.householdID, 'Investment'));
      await queryClient.invalidateQueries(ttDetailsKeys.base);
    },
  });

  return { closeInvestment };
};
